import { maybe, Maybe } from '@passionware/monads';
import { Nullable } from 'v5/platform/typescript';

/**
 * Layer 2 — browsing scope: list API workspace filters from resolved app scope (URL).
 *
 * @see .agents/rules/workspace-scoping-layers.md
 */

export type WorkspaceScoping = { workspaceId: Nullable<number> };

export type WithWorkspaceScoping<T> = T & WorkspaceScoping;

/** Discriminated app-scope shape (resolved or identity) for workspace stamps. */
export type WorkspaceScopingSource =
  | { level: 'organization' }
  | { level: 'workspace' | 'team'; workspaceId: number };

export const workspaceScopingQueryUtils = {
  ofGlobal: (): WorkspaceScoping => ({ workspaceId: null }),
  ofWorkspace: (workspaceId: number): WorkspaceScoping => ({ workspaceId }),
  fromScope: (scope: WorkspaceScopingSource): WorkspaceScoping => {
    if (scope.level === 'organization') {
      return workspaceScopingQueryUtils.ofGlobal();
    }
    return workspaceScopingQueryUtils.ofWorkspace(scope.workspaceId);
  },
  /**
   * Layer 3 — a 1:1 entity's own workspace as the scope of a list for it (a callsheet's
   * picker): that workspace, the organization for an entity of none (`null`). Absent while the
   * entity isn't loaded, or when its copy doesn't say (a list's copy without the column): never
   * the organization by default.
   */
  ofEntity: (
    entity: Maybe<{ enterprise_workspace_id?: Nullable<number> }>
  ): Maybe<WorkspaceScoping> => {
    const workspaceId = entity?.enterprise_workspace_id;
    if (workspaceId === undefined) return maybe.ofAbsent();
    return workspaceId === null
      ? workspaceScopingQueryUtils.ofGlobal()
      : workspaceScopingQueryUtils.ofWorkspace(workspaceId);
  }
};

/** Workspace id from browsing scope; absent on org / missing scope. */
export function workspaceIdFromScope(
  scope: Maybe<WorkspaceScopingSource>
): Maybe<number> {
  return maybe.map(scope, s =>
    s.level === 'organization' ? maybe.ofAbsent() : s.workspaceId
  );
}

export function withWorkspaceScoping<T>(
  searchQuery: T,
  scoping: WorkspaceScoping
): WithWorkspaceScoping<T> {
  return { ...searchQuery, ...scoping };
}

export function asGlobalResourceQuery<T>(
  searchQuery: T
): WithWorkspaceScoping<T> {
  return withWorkspaceScoping(
    searchQuery,
    workspaceScopingQueryUtils.ofGlobal()
  );
}
